import { InfoIcon } from '@chakra-ui/icons';
import {
  Alert,
  AlertIcon,
  AlertTitle,
  Button,
  HStack,
  Icon,
  ListItem,
  Stack,
  Text,
  Tooltip,
  UnorderedList,
} from '@chakra-ui/react';
import { ExclamationIcon } from '@heroicons/react/outline';
import { DocumentDownloadIcon, LightningBoltIcon } from '@heroicons/react/solid';
import { useParams } from 'react-router-dom';
import { useTranslations } from 'use-intl';

import { noop } from '@blockpulse3/data/shared';
import { IdentityType, useGetSubscriptionQuery } from '@blockpulse3/graphql/hooks';
import { IconBoxWrapper } from '@blockpulse3/ui/commons';

import { useUser } from '../../../../providers';
import { IVerificationType } from './types';

type Props = {
  setVerificationType: React.Dispatch<React.SetStateAction<IVerificationType>>;
};

export function SelectVerificationType({ setVerificationType = noop }: Props): JSX.Element {
  const i18nSelectVerificationType = useTranslations('SelectVerificationType');
  const i18nTooltip = useTranslations('DocumentVerification.Tooltip');

  const { user } = useUser();

  const { subscriptionId = '' } = useParams();

  const { data } = useGetSubscriptionQuery({
    variables: { subscriptionId },
    skip: !subscriptionId,
  });

  const presidentIndividualIdentity =
    data?.subscription?.identity?.companyIdentity?.kybIndividualIdentity;

  const handleUbbleVerification = (): void => {
    setVerificationType('ubble');
  };

  const handleDocumentVerification = (): void => {
    setVerificationType('document');
  };

  const shouldDisplayBanner = (): boolean => {
    if (!data?.subscription) {
      return false;
    }

    if (data.subscription.identity.type === IdentityType.INDIVIDUAL) {
      return false;
    }

    return presidentIndividualIdentity?.id !== user?.individualIdentity?.id;
  };

  /* ** Clear way to return default values in auth case section (no subscriptionId) ** */
  const shouldBlockVerification = shouldDisplayBanner();
  const presidentName = presidentIndividualIdentity?.name || '';

  return (
    <Stack spacing="5" w="full">
      {shouldBlockVerification && (
        <Alert status="warning">
          <AlertIcon as={ExclamationIcon} />
          <AlertTitle>
            {i18nSelectVerificationType.rich('WarningBanner', {
              identity: presidentName,
              important: (chunk) => (
                <Text as="span" fontWeight="bold">
                  {chunk}
                </Text>
              ),
            })}
          </AlertTitle>
        </Alert>
      )}
      <Button
        isDisabled={shouldBlockVerification}
        pl="24px"
        variant="container"
        onClick={handleUbbleVerification}
      >
        <IconBoxWrapper mr="24px">
          <Icon as={LightningBoltIcon} boxSize="20px" color="indigo.600" minW="50px" />
        </IconBoxWrapper>
        <Stack>
          <Text>{i18nSelectVerificationType('MobileVerification')}</Text>
          <UnorderedList color="gray.700" fontSize="sm" fontWeight="light" pl="1.5">
            <ListItem>{i18nSelectVerificationType('MobileList1')}</ListItem>
            <ListItem>{i18nSelectVerificationType('MobileList2')}</ListItem>
          </UnorderedList>
        </Stack>
      </Button>
      <Button
        isDisabled={shouldBlockVerification}
        pl="24px"
        variant="container"
        onClick={handleDocumentVerification}
      >
        <IconBoxWrapper mr="24px">
          <Icon as={DocumentDownloadIcon} boxSize="20px" color="gray.700" minW="50px" />
        </IconBoxWrapper>
        <Stack>
          <Text>{i18nSelectVerificationType('DocumentVerification')}</Text>
          <UnorderedList color="gray.700" fontSize="sm" fontWeight="light" pl="1.5">
            <ListItem>{i18nSelectVerificationType('DocumentList1')}</ListItem>
            <HStack>
              <ListItem>{i18nSelectVerificationType('DocumentList2')}</ListItem>
              <Tooltip
                hasArrow
                placement="top"
                label={
                  <UnorderedList>
                    <ListItem>{i18nTooltip('ListItem1')}</ListItem>
                    <ListItem>{i18nTooltip('ListItem2')}</ListItem>
                  </UnorderedList>
                }
              >
                <Icon as={InfoIcon} color="gray.600" />
              </Tooltip>
            </HStack>
            <ListItem>{i18nSelectVerificationType('DocumentList3')}</ListItem>
          </UnorderedList>
        </Stack>
      </Button>
    </Stack>
  );
}
